import './header';
import './placeholder';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import apiFetch from '@wordpress/api-fetch';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x, sprintf } from '@wordpress/i18n';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware( UncannyAutomatorBackend.rest.nonce ) );

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * The AutomatorLog class is a LitElement that renders a log for a specific recipe run. It retrieves data from the
 * server and displays it in a structured way, including triggers, actions, and filters. It also allows the user to
 * refresh the data, download it, and delete the log.
 */
@customElement( 'uap-log' ) // eslint-disable-line require-jsdoc
export class AutomatorLog extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Number, attribute: 'log-id' } ) accessor logID;

	@property( { type: Number, attribute: 'recipe-id' } ) accessor recipeID;

	@property( { type: Number, attribute: 'run-number' } ) accessor runNumber;

	// The main object of the recipe
	@property( { type: Object, attribute: false } ) accessor data = {};

	// The main object of the sidebar
	@property( { type: Object, attribute: false } ) accessor sidebar = {};

	// The object that contains information about the snackbar of this component
	// {
	//     message: 'Hello world',
	//     icon: { // optional
	//         id: 'loading',
	//         animation: 'spin', // optional
	//     }
	// }
	@property( { type: Object, attribute: false } ) accessor snackbar = {};

	// A global error message. Only used if we can't load the data for some reason
	@property( { type: String, attribute: false } ) accessor fatalErrorMessage = '';

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		// Check if the required properties are defined
		if ( ! this.isImportMode && isEmpty( this.logID ) ) {
			return html`${ sprintf(
				/* translators: 1. The attribute name */
				escapeHTML( __( 'Error: The required attribute "%1$s" is missing', 'uncanny-automator' ) ),
				'log-id'
			) }`;
		}

		if ( ! this.isImportMode && isEmpty( this.recipeID ) ) {
			return html`${ sprintf(
				/* translators: 1. The attribute name */
				escapeHTML( __( 'Error: The required attribute "%1$s" is missing', 'uncanny-automator' ) ),
				'recipe-id'
			) }`;
		}

		if ( ! this.isImportMode && isEmpty( this.runNumber ) ) {
			return html`${ sprintf(
				/* translators: 1. The attribute name */
				escapeHTML( __( 'Error: The required attribute "%1$s" is missing', 'uncanny-automator' ) ),
				'run-number'
			) }`;
		}

		// If there is data available, render it and stop here
		if ( ! isEmpty( this.data ) ) {
			return this._templateMain;
		}

		// Check if there is an error
		if ( ! isEmpty( this.fatalErrorMessage ) ) {
			return this._templateFatalError;
		}

		// Otherwise, get the main object. In the meantime, render the placeholder
		// We're using this approach instead of using the native `.until` directive to prevent Lit from
		// rendering the whole UI when the Promise is resolved, and then AGAIN when one of the reactive
		// properties change (like `.sidebar`).
		return this.initialLoadPlaceholder();
	}

	/**
	 * Retrieves and returns the data needed to render the logs
	 *
	 * @return {TemplateResult} The placeholder template
	 */
	initialLoadPlaceholder() {
		// Start call
		this.getData().catch( error => {
			// Show a global error
			this.fatalErrorMessage = ! isEmpty( error.message )
				? error.message
				: escapeHTML( __( 'Unknown error', 'uncanny-automator' ) );

			console.error( error );
		} );

		// In the meantime, show the placeholder
		return html`<uap-log-placeholder></uap-log-placeholder>`;
	}

	/**
	 * Returns the Promise that retrieves the data
	 *
	 * @return {Promise} The Promise that retrieves the data
	 */
	getData() {
		return apiFetch( {
			url: `${ UncannyAutomatorBackend.rest.base }automator/v1/log/recipe_id/${ this.recipeID }/run_number/${ this.runNumber }/recipe_log_id/${ this.logID }`
		} )
			.then( response => {
				if ( ! response.success && isDefined( response.error ) ) {
					// Show a global error
					this.fatalErrorMessage = ! isEmpty( response.error.message ) ?
						response.error.message :
						escapeHTML( __( 'Unknown error', 'uncanny-automator' ) );

					console.error( response?.error );

					return;
				}

				// Cache the data
				this.data = response.data;
			} );
	}

	/**
	 * Handles the data refresh when there is already data available
	 *
	 * @param {CustomEvent} event The event that requests the data refresh
	 */
	handleDataRefresh( event ) {
		// Show the Snackbar to notify the user that we're updating the data
		this.snackbar = {
			action: 'open',
			message: escapeHTML( __( 'Reloading this log', 'uncanny-automator' ) ),
			icon: {
				id: 'loading',
				animation: 'spin'
			}
		}

		// Start call
		this.getData()
			.then( response => {
				// Remove the Snackbar
				this.snackbar = false;
			} )
			.catch( error => {
				// Remove the Snackbar
				this.snackbar = false;

				console.error( error );
			} );
	}

	/**
	 * Resets the cached data if one of the properties that affect it change.
	 *
	 * `this.data` is a reactive property too, but .willUpdate will be invoked before rendering,
	 * so this won't make Lit render the UI twice
	 *
	 * @param {Map} changedProperties The changed properties
	 */
	willUpdate( changedProperties ) {
		if (
			! isEmpty ( this.data ) &&
			(
				changedProperties.has( 'logID' ) ||
				changedProperties.has( 'recipeID' ) ||
				changedProperties.has( 'runNumber' )
			)
		) {
			this.data = null;
			this.sidebar = null;
		}
	}

	/**
	 * Requests the component to show the sidebar
	 *
	 * @param {CustomEvent} event The event that requests the sidebar change
	 */
	handleRequestSidebar( event ) {
		// Check if the action (open/close) is defined
		if ( ! isDefined( event.detail.action ) ) {
			return;
		}

		// Check if we have to hide it
		if ( event.detail.action === 'close' ) {
			// Notify other elements that the sidebar is now closed
			this.dispatchEvent(
				new CustomEvent( 'uap-log-sidebar-visibility-changed', {
					detail: { isOpen: false }
				} )
			);

			this.sidebar = null;
			return;
		}

		// Check if the data required to render the sidebar was sent
		if ( ! isDefined( event.detail.sourceID ) || ! isDefined( event.detail.content ) ) {
			return;
		}

		// Check if it was opened before
		if ( isEmpty( this.sidebar ) ) {
			// Notify other elements that the sidebar is now opened
			this.dispatchEvent(
				new CustomEvent( 'uap-log-sidebar-visibility-changed', {
					detail: { isOpen: true }
				} )
			);
		}

		this.sidebar = {
			sourceID: event.detail.sourceID,
			content: event.detail.content
		};
	}

	/**
	 * Handles the request made by other components to show the snackbar
	 *
	 * @param {CustomEvent} event The event that requests changes to the snackbar
	 */
	handleSnackbar( event ) {
		// Check if the required data is defined
		this.snackbar = event.detail;
	}

	/**
	 * Returns the main template, containing all parts
	 *
	 * @return {TemplateResult} The main template
	 */
	get _templateMain() {
		return html`
			<div 
				class="log"
				@uap-log-request-sidebar=${ this.handleRequestSidebar }
				@uap-log-request-data-refresh=${ this.handleDataRefresh }
				@uap-log-request-snackbar=${ this.handleSnackbar }
			>
				${ this._templateSnackbar }

				<div class="log-body">
					${ this._templateHeader }
					${ this._templateTriggers }
					${ this._templateActions }
				</div>

				${ this._templateSidebar }
			</div>
		`;
	}

	/**
	 * Returns the template that displays an error when there is no data at all
	 *
	 * @return {TemplateResult} The error message
	 */
	get _templateFatalError() {
		return html`
			<uo-alert
				type="error"
				heading=${ escapeHTML( __( 'Something went wrong', 'uncanny-automator' ) ) }
			>
				<p>${ this.fatalErrorMessage }</p>

				<uo-button
					size="small"
					color="secondary"

					@click=${ () => {
						// Remove message
						// This will re render the component, and given that there is no data, it'll do the call again
						this.fatalErrorMessage = '';
						this.data = null;
					} }
				>
					${ escapeHTML( _x( 'Try again', 'Button label', 'uncanny-automator' ) ) }
				</uo-button>
			</uo-alert>
		`;
	}

	/**
	 * Returns the template that renders the Snackbar of this component
	 *
	 * @return {TemplateResult|undefined} The snackbar
	 */
	get _templateSnackbar() {
		if ( isEmpty( this.snackbar ) ) {
			return;
		}

		return html`
			<div class="log-snackbar">
				<uo-snackbar 
					message=${ this.snackbar.message }
					duration="-1"
				>
					${
						isDefined( this.snackbar.icon ) ?
						html`
							<uo-icon 
								slot="icon"
								id=${ this.snackbar.icon.id }
								animation=${ ifDefined( this.snackbar.icon.animation ) }
							></uo-icon>
						`:
						''
					}
				</uo-snackbar>
			</div>
		`;
	}

	/**
	 * Returns the template of the header of the dialog. This shows:
	 * 1. Recipe name
	 * 2. Recipe run number
	 * 3. User data
	 * 4. The recipe status
	 * 5. Run date and time
	 *
	 * @return {TemplateResult} The header
	 */
	get _templateHeader() {
		// Create summary array
		const recipeSummary = [];

		// Check if recipe was triggered by a user
		const wasTriggeredByUser = ! isEmpty( this.data.triggered_by_user ) && this.data.triggered_by_user !== 0;

		// Check if it was triggered by a user
		if ( wasTriggeredByUser ) {
			// Show the user data
			recipeSummary.push( {
				key:   escapeHTML( __( 'User', 'uncanny-automator' ) ),
				value: ! this.isImportMode ?
					html`<uo-user-card user-id="${ this.data.triggered_by_user }"></uo-user-card>` :
					html`${ sprintf(
						/* translators: 1. The user ID */
						escapeHTML( __( 'User ID #%1$s', 'uncanny-automator' ) ),
						this.data.triggered_by_user
					) }`,
			} );
		}

		// Recipe status
		recipeSummary.push( {
			key:   escapeHTML( __( 'Status', 'uncanny-automator' ) ),
			value: html`<uap-log-status status-id="${ this.data.status_id }"></uap-log-status>`
		} );

		// Start date
		recipeSummary.push( {
			key:   escapeHTML( __( 'Start date', 'uncanny-automator' ) ),
			value: this.data.start_date
		} );

		// End date
		if ( ! isEmpty( this.data.end_date ) ) {
			recipeSummary.push( {
				key:   escapeHTML( __( 'End date', 'uncanny-automator' ) ),
				value: this.data.end_date
			} );
		}

		// Check if it was triggered by a user
		if ( wasTriggeredByUser ) {
			// Show the user run number
			recipeSummary.push( {
				key:   escapeHTML( __( 'User run number', 'uncanny-automator' ) ),
				value: this.data.run_number
			} );
		}

		return html`
			<uap-log-header
				recipe-title=${ this.data.title  }
				run-number=${ this.data.run_number }
				.summary=${ recipeSummary }

				recipe-edit-url=${ this.data.recipe_edit_url }
				log-delete-url=${ this.data.log_delete_url }
				recipe-status-id=${ this.data.status_id }

				?import-mode=${ this.isImportMode }
				?sidebar-open=${ ! isEmpty( this.sidebarSourceID ) }

				.downloadObject=${ this.data }
			></uap-log-header>
		`;
	}

	/**
	 * Returns the template that shows the list of triggers
	 *
	 * @return {TemplateResult} The triggers section
	 */
	get _templateTriggers() {
		return html`
			<div class="log-triggers">
				<h2 class="log-section-title">${ escapeHTML( __( 'Triggers', 'uncanny-automator' ) ) } ${ this.data.triggers.items.length > 1 ? html`<uo-chip filled>${ this.data.triggers.logic === 'ANY' ? escapeHTML( _x( 'Any', 'Trigger', 'uncanny-automator' ) ) : escapeHTML( _x( 'All', 'Trigger', 'uncanny-automator' ) ) }</uo-chip>` : '' }</h2>

				<div class="log-section-items">
					${ repeat(
						this.data.triggers.items,
						item => item.id,
						( item, index ) => {
							return html`
								<uap-log-item-trigger 
									item-id=${ item.id }
									item-code=${ item.code }

									integration-code=${ item.integration_code }

									status-id=${ item.status_id }

									start-date=${ item.start_date }
									end-date=${ item.end_date }
									
									.titleHTML=${ item.title_html }
									.titleCustomName=${ item.title_custom_name || '' }
									.fields=${ item.fields }
									.runs=${ item.runs }

									?is-open=${ this.sidebarSourceID === `item-${ item.id }` }

									?from-legacy-log=${ item.from_legacy_log }
									?import-mode=${ this.isImportMode }
								></uap-log-item-trigger>
							`;
						}
					) }
				</div>
			</div>
		`;
	}

	/**
	 * Returns the template that shows the list of actions, filters and loops
	 *
	 * @return {TemplateResult} The actions section. This includes filters and loops too
	 */
	get _templateActions() {
		return html`
			<div class="log-actions">
				<h2 class="log-section-title">${ escapeHTML( __( 'Actions', 'uncanny-automator' ) ) }</h2>

				<div class="log-section-items">
					${ repeat(
						this.data.actions.items,
						item => item.id,
						( item, index ) => {
							// Check what element we have to render
							if ( item.type === 'action' ) {
								return html`
									<uap-log-item-action 
										item-id=${ item.id }
										item-log-id=${ item.item_log_id }
										item-code=${ item.code }

										integration-code=${ item.integration_code }

										status-id=${ item.status_id }

										start-date=${ item.start_date }
										end-date=${ item.end_date }

										.resultMessage=${ item.result_message }
										
										.titleHTML=${ item.title_html }
										.titleCustomName=${ item.title_custom_name || '' }
										.fields=${ item.fields }
										.runs=${ item.runs }

										?is-open=${ this.sidebarSourceID === `item-${ item.id }` }
										?can-rerun=${ item.can_rerun }
										?can-run-now=${ item.can_run_now }
										?can-cancel=${ item.can_cancel }

										?from-legacy-log=${ item.from_legacy_log }
										?import-mode=${ this.isImportMode }
									></uap-log-item-action>`;
							}

							if ( item.type === 'filter' ) {
								return html`
									<uap-log-filter 
										filter-id="${ item.id }"
										logic="${ item.logic }"
										sidebar-source-id="${ this.sidebarSourceID }"

										.conditions=${ item.conditions }
										.items=${ item.items }

										?passed=${ this.conditions_fullfilled }
										?import-mode=${ this.isImportMode }
									></uap-log-filter>
								`;
							}

							if ( item.type === 'loop' ) {
								return html`
									<uap-log-loop 
										loop-id=${ item.id }
										loop-process-code=${ `loop_process_${ item.id }_${ this.recipeID }_${ this.logID }_${ this.runNumber }` }
										
										logic=${ item.logic }

										start-date=${ item.start_date }
										end-date=${ item.end_date }

										.loopFilters=${ item.loops_filters }
										.items=${ item.items }

										.status=${ item.status }

										.iterableExpression=${ item.iterable_expression }

										.runOn=${ item.run_on }

										.dateElapsed=${ item.date_elapsed }
										.dateNextProcess=${ item.date_next_process }

										sidebar-source-id="${ this.sidebarSourceID || '' }"
										?import-mode=${ this.isImportMode }
									></uap-log-loop>
								`;
							}

							if ( item.error === true && ! isEmpty( item.message ) ) {
								return html`
									<uo-alert
										type="error"
									>
										${ item.message }    
									</uo-alert>
								`;
							}
						}
					) }
				</div>
			</div>
		`;
	}

	/**
	 * Returns the template that shows the sidebar
	 *
	 * @return {TemplateResult|undefined} The sidebar
	 */
	get _templateSidebar() {
		// Check if there is sidebar data
		if ( isEmpty( this.sidebar ) ) {
			return;
		}

		return html`
			<div class="log-sidebar">
				<uap-log-sidebar
					.title="${ this.sidebar.content.title }"
					.summary=${ this.sidebar.content.summary }
					.runs=${ this.sidebar.content.runs }
					.miscellaneous=${ this.sidebar.content.miscellaneous }
				></uap-log-sidebar>
			</div>
		`;
	}

	/**
	 * Returns the ID of the source of the data the sidebar is showing
	 *
	 * @return {String|undefined} The ID of the source of the data the sidebar is showing
	 */
	get sidebarSourceID() {
		if ( ! isDefined( this.sidebar ) ) {
			return;
		}

		if ( ! isDefined( this.sidebar.sourceID ) ) {
			return;
		}

		return this.sidebar.sourceID;
	}
}
